import {
  TourbookListing,
  TourbookListingAnalyticsInformation,
  ExternalListingAnalyticsInformation,
  StoreState,
} from '@root/types';
import Title from '@components/layouts/Truva/Tourbook/Title';
import ExclusiveLabel from '@components/shared/ExclusiveLabel';
import { Link } from 'react-router-dom';
import ListingShareUrlHelper from '@shared/listingShareUrlHelper';
import HTMLComment from 'react-html-comment';
import {
  Button,
  ButtonLink,
  CustomIcon,
  OnlyInDesktopTablet,
  OnlyInMobile,
} from '@components/shared';
import { PARAMETERS } from '@root/tracking/constants';
import routes from '@root/routes';
import TourbookMosaicGallery from '@components/layouts/Truva/Tourbook/TourbookMosaicGallery';
import { useTranslation, Trans } from 'react-i18next';
import actions from '@store/actions/tourbookPage';
import { connect } from 'react-redux';
import { hasCurrentUser } from '@root/store/selectors';
import ListingAttributes from '../ListingAttributes';
import ListingDescription from '../ListingDescription';
import s from './TourbookListingCard.module.less';

const ViewListingButton = ({ onClick, url }: { onClick: () => void; url: string }) => {
  const { t } = useTranslation('tourbook');

  return (
    <ButtonLink onClick={onClick} href={url} type="secondary" size="medium" openInNewTab>
      {t('listingCard.viewListingLabel')}
    </ButtonLink>
  );
};
const mapeStateToProps = (state: StoreState) => ({
  userIsLoggedIn: hasCurrentUser(state),
});
const mapDispatch = {
  tourbookViewListing: listingAnalyticsInformation =>
    actions.tourbookViewListing(listingAnalyticsInformation),
  trackLightboxThumbnailClick: listingAnalyticsInformation =>
    actions.lightboxThumbnailClicked(listingAnalyticsInformation),
  trackLightboxPagination: listingAnalyticsInformation =>
    actions.lightboxPaginationClicked(listingAnalyticsInformation),
  trackMediaAssetOpening: listingAnalyticsInformation =>
    actions.mediaAssetOpened(listingAnalyticsInformation),
  trackMapDirectionsClicked: listingAnalyticsInformation =>
    actions.tourbookListingMapDirectionsClicked(listingAnalyticsInformation),
  tourbookEditExternalListing: externalListingAnalyticsInformation =>
    actions.tourbookEditExternalListing(externalListingAnalyticsInformation),
  trackLightboxFloorPlanClick: (listingAnalyticsInformation, external) =>
    actions.floorplanOpened(listingAnalyticsInformation, external),
};

const connector = connect(mapeStateToProps, mapDispatch);

type TourbookListingCardProps = {
  tourbookId: string;
  listing: TourbookListing;
  ownerId: string;
  userIsLoggedIn: boolean;
  handleOnRemoveListingClick: () => void;
  onHover?: () => void;
  onHoverExit?: () => void;
  readOnly: boolean;
  tourbookViewListing: (listingAnalyticsInformation: TourbookListingAnalyticsInformation) => void;
  trackLightboxThumbnailClick: (
    listingAnalyticsInformation: TourbookListingAnalyticsInformation,
  ) => void;
  trackLightboxPagination: (
    listingAnalyticsInformation: TourbookListingAnalyticsInformation,
  ) => void;
  trackLightboxFloorPlanClick: (
    listingAnalyticsInformation: TourbookListingAnalyticsInformation,
    external: boolean,
  ) => void;
  trackMapDirectionsClicked: (
    listingAnalyticsInformation: TourbookListingAnalyticsInformation,
  ) => void;
  tourbookEditExternalListing: (
    externalListingAnalyticsInformation: ExternalListingAnalyticsInformation,
  ) => void;
};

export const TourbookListingCard = ({
  tourbookId,
  listing,
  ownerId,
  userIsLoggedIn,
  handleOnRemoveListingClick,
  onHover = () => {},
  onHoverExit = () => {},
  readOnly,
  tourbookViewListing = () => {},
  trackLightboxThumbnailClick,
  trackLightboxPagination,
  trackLightboxFloorPlanClick,
  trackMapDirectionsClicked,
  tourbookEditExternalListing,
}: TourbookListingCardProps) => {
  const { t } = useTranslation('tourbook');

  const { building, description, id: listingId, flaggedAt } = listing;

  const listingIdToUpdate = (
    listing.type === 'externalListing'
      ? listing.externalTourbookListingId
      : listing.tourbookListingId
  ) as string;

  const encodedData = ListingShareUrlHelper.encode({
    listingId,
    userId: ownerId,
    hideLocation: false,
  });
  const viewListingUrl = userIsLoggedIn
    ? routes.listing(listingId, building.id)
    : routes.listingShare(encodedData);

  const onViewListing = () => {
    tourbookViewListing(listing.analyticsInformation);
  };

  const onOpenDirections = () => {
    trackMapDirectionsClicked(listing.analyticsInformation);
  };

  const openMapDirections = location => {
    const baseMapsURL = `https://www.google.com/maps/dir/?api=1&destination=`;
    const url = `${baseMapsURL}${location.latitude},${location.longitude}`;
    onOpenDirections();
    window.open(url, '_blank');
  };

  const mapButton = location => {
    return (
      <CustomIcon
        className={s.directionsIcon}
        type="location"
        onClick={() => openMapDirections(location)}
      />
    );
  };

  if (readOnly && flaggedAt) {
    return null;
  }

  return (
    <div className={s.container} onMouseEnter={onHover} onMouseLeave={onHoverExit}>
      <HTMLComment text={`${listing.external ? 'External ' : ''}Listing ID: ${listing.id}`} />
      <div className={flaggedAt ? s.flagged : undefined}>
        <div className={s.titleLinkAndDeleteIcon}>
          <Title
            displayAddress={building.displayAddress}
            listingName={listing.name}
            primaryName={building.primaryName}
            cityLocalityRegion={building.cityLocalityRegion}
            neighborhood={building.neighborhood}
            countryCode={building.countryCode}
            directionsButton={mapButton(listing)}
          />
          <div data-testid="titleButtons">
            <div className={s.linkAndDeleteIcon}>
              <OnlyInDesktopTablet className={s.inlineViewportWrapper}>
                {!listing.external && (
                  <ViewListingButton onClick={onViewListing} url={viewListingUrl} />
                )}
                {listing.external && !readOnly ? (
                  <Link
                    to={routes.tourbookEditExternalListing(tourbookId, listingId)}
                    data-testid="editLink"
                    onClick={() => {
                      tourbookEditExternalListing(
                        listing.analyticsInformation as ExternalListingAnalyticsInformation,
                      );
                    }}
                  >
                    <CustomIcon className={s.deleteIcon} type="edit" />
                  </Link>
                ) : null}
                {!readOnly && (
                  <CustomIcon
                    className={s.deleteIcon}
                    type="trash"
                    onClick={handleOnRemoveListingClick}
                    data-testid="trashIcon"
                  />
                )}
              </OnlyInDesktopTablet>
            </div>
          </div>
        </div>
        <div className={s.mosaicGalleryContainer}>
          {listing.isCurrentlyExclusive && listing.status === 'on_market' ? (
            <div className={s.exclusiveLabelContainer}>
              <ExclusiveLabel size="medium" />
            </div>
          ) : null}
          {listing.status === 'off_market' ? (
            <div className={s.offMarket}>{t('listingCard.offMarket').toUpperCase()}</div>
          ) : null}
          <TourbookMosaicGallery
            photos={listing.photos}
            video={listing.video}
            sourceContent={PARAMETERS.tourbookListingGallery}
            sourcePage={PARAMETERS.tourbookPage}
            trackLightboxPagination={() => trackLightboxPagination(listing.analyticsInformation)}
            trackLightboxThumbnailClick={() =>
              trackLightboxThumbnailClick(listing.analyticsInformation)
            }
            trackLightboxFloorPlanClick={() => {
              trackLightboxFloorPlanClick(listing.analyticsInformation, listing.external);
            }}
            imagesZipUrl={listing.createImageZipUrl}
            tourEmbedUrl={listing.tourEmbedUrl}
          />
        </div>
        <ListingAttributes
          askingRentInMoneyPerPeriod={listing.askingRentInMoneyPerPeriod}
          askingRentInMoneyPerAreaPerPeriod={listing.askingRentInMoneyPerAreaPerPeriod}
          condition={listing.condition}
          minMaxArea={listing.minMaxArea}
          dateAvailable={listing.dateAvailable}
          availableToday={listing.availableToday}
          leaseType={listing.leaseType}
          size={listing.size}
        />
        <OnlyInMobile>
          <div className={s.linkAndDeleteIcon}>
            {!listing.external && (
              <ViewListingButton onClick={onViewListing} url={viewListingUrl} />
            )}
            {!readOnly && (
              <CustomIcon
                className={s.deleteIcon}
                type="trash"
                onClick={handleOnRemoveListingClick}
                data-testid="trashIcon"
              />
            )}
          </div>
        </OnlyInMobile>
        <ListingDescription
          description={description}
          readOnly={readOnly}
          tourbookListingId={listingIdToUpdate}
        />
      </div>
      {flaggedAt && (
        <>
          <div className={s.flaggedOverlay} />
          <div className={s.flaggedDialogContainer}>
            <div className={s.flaggedDialog}>
              <div>
                <CustomIcon className={s.flaggedIcon} type="info" />
              </div>
              <div>
                {/* eslint-disable react/jsx-no-literals */}
                <Trans i18nKey="flaggedContent.message" ns="tourbook">
                  This entry has been flagged as containing potentially infringing content. Please
                  contact
                  <a href="mailto:support@vts.com"> support@vts.com </a>
                  for more information.
                </Trans>
                {/* eslint-enable react/jsx-no-literals */}
              </div>
              <div>
                <Button type="secondary" size="small" onClick={handleOnRemoveListingClick}>
                  {t('flaggedContent.delete')}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default connector(TourbookListingCard);
